import _ from 'lodash';
import {
  ReactNode,
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useController } from 'react-hook-form';
import Select, { MenuPlacement, MultiValue, SingleValue } from 'react-select';

import { SELECT_STYLE } from 'app/constants';
import { OptionItem } from 'app/models/Common';
import cn from 'app/utils/ClassName';

export interface SelectFieldProps {
  name: string;
  label?: string | ReactNode;
  isMulti?: boolean;
  options: OptionItem[];
  placeholder?: string;
  defaultValue?: string;
  isDisable?: boolean;
  containerClassName?: string;
  customNoOptionMessage?: string;
  inputClassName?: string;
  labelClassName?: string;
  onMenuScrollToBottom?: () => void;
  onInputChange?: (e: string) => void;
  closeMenuOnSelect?: boolean;
  isClearable?: boolean;
  onChangeCallBack?: (value?: (string | number)[] | string | number) => void
  menuPlacement?: MenuPlacement;
}

export const SelectField = ({
  name,
  label,
  isMulti = false,
  options,
  placeholder,
  defaultValue,
  isDisable = false,
  containerClassName,
  inputClassName,
  labelClassName,
  onMenuScrollToBottom,
  onInputChange,
  closeMenuOnSelect = true,
  isClearable = false,
  onChangeCallBack,
  customNoOptionMessage,
  menuPlacement,
}: SelectFieldProps) => {
  const [inputValue, setInputValue] = useState<string>('');
  const [localOptions, setLocalOptions] = useState<OptionItem[]>([]);

  useEffect(() => {
    setLocalOptions((prev) => _.uniqBy([...prev, ...options], (option) => option.value));
  }, [options]);

  const { field, fieldState } = useController({
    name,
    defaultValue,
  });

  const rootClassName = cn(
    'relative w-full border-border-brand focus:border-border-brand focus:outline-none focus:ring-0 flex-warp',
    'bg-white text-[16px] leading-[19.36px] tracking-[0.01em] text-text-primary placeholder:text-text-light',
    inputClassName,
  );

  const searchOnChange = (text: string) => {
    setInputValue(text);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceSearch = useCallback(
    _.debounce(onInputChange ?? (() => {}), 500),
    [],
  );

  const foundOption = useMemo(
    () => {
      if (isMulti) {
        return localOptions.filter((option) => field.value?.includes(option.value)) || [];
      }
      if (field.value) {
        return localOptions.find((option) => option.value === field.value) || null;
      }
      return null;
    },
    [field.value, isMulti, localOptions],
  );

  useEffect(() => {
    debounceSearch(inputValue);
  }, [inputValue, debounceSearch]);

  return (
    <div className={cn('relative space-y-[8px]', containerClassName)} id={name}>
      <div className="flex flex-wrap items-end justify-between">
        {label ? (
          <label
            htmlFor={name}
            className={cn(
              'relative block text-[14px] font-semibold leading-[16.94px] text-text-primary',
              labelClassName,
            )}
          >
            {label}
          </label>
        ) : (
          <div />
        )}
      </div>
      <div
        className={cn(
          'relative',
          isDisable ? 'cursor-not-allowed !bg-bg-disabled' : '',
        )}
      >
        <Select
          {...field}
          isMulti={isMulti}
          value={foundOption}
          onChange={isMulti ? (newValue: MultiValue<OptionItem>) => {
            field.onChange(newValue.map((option) => option.value));
            onChangeCallBack?.(newValue.map((option) => option.value));
          } : (
            newValue: SingleValue<OptionItem>,
          ) => {
            field.onChange(newValue?.value || null);
            onChangeCallBack?.(newValue?.value);
          }}
          classNamePrefix="select2-selection"
          menuPlacement={menuPlacement || 'auto'}
          options={options}
          isDisabled={isDisable}
          className={rootClassName}
          placeholder={placeholder || label}
          name={name}
          styles={SELECT_STYLE}
          noOptionsMessage={() => customNoOptionMessage || '沒有選項'}
          onMenuScrollToBottom={onMenuScrollToBottom}
          inputValue={inputValue}
          onInputChange={searchOnChange}
          isClearable={isClearable}
          closeMenuOnSelect={closeMenuOnSelect}
        />
      </div>

      {fieldState.error ? (
        <p className="text-left text-[12px] font-normal leading-[16px] tracking-[0.01em] text-brand-attention">
          {fieldState.error.message}
        </p>
      ) : (
        <div className="h-[16px]" />
      )}
    </div>
  );
};

export default SelectField;
